import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { db } from '@/lib/db';
import { api } from '@/lib/api';
import { storage } from '@/lib/storage';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface ChatMessage {
  id: string;
  sender_id: string;
  recipient_id: string;
  content: string | null;
  attachment_path: string | null;
  attachment_name: string | null;
  attachment_type: string | null;
  attachment_expires_at: string | null;
  is_read: boolean;
  created_at: string;
}

export interface ChatContact {
  user_id: string;
  nome_completo: string;
  setor: string | null;
  unread_count: number;
  last_message?: Pick<ChatMessage, 'id' | 'sender_id' | 'content' | 'attachment_name' | 'created_at'>;
}

const MESSAGES_PAGE_SIZE = 50;

/**
 * Hook principal do sistema de chat
 * Consolida: contatos, conversas, contagem de não lidas.
 *
 * Conversas vêm agregadas do servidor (/api/db/chat-conversations): 1 linha por
 * contato com última mensagem + não lidas + nome/setor já resolvidos. Antes o
 * cliente baixava TODO o histórico e agrupava em JS — ver auditoria A1.
 */
export const useChat = () => {
  const { user } = useAuth();

  // Lista todos os usuários ativos (exceto o atual) via RPC — usada só para
  // iniciar conversa nova; a lista de conversas não depende mais dela.
  const { data: contacts = [], isLoading: contactsLoading, error: contactsError } = useQuery({
    queryKey: ['chat-contacts'],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await db.rpc('get_chat_contacts');
      if (error) throw error;
      return (data || []).sort((a, b) =>
        a.nome_completo.localeCompare(b.nome_completo)
      );
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  const { data: conversations = [], isLoading: conversationsLoading } = useQuery({
    queryKey: ['chat-conversations', user?.id],
    queryFn: async () => {
      const { data, error } = await api.invoke<ChatContact[]>('db/chat-conversations', { body: {} });
      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
    refetchInterval: false, // Realtime ativo via useChatSync
  });

  // Contagem total de não lidas
  const unreadCount = conversations.reduce((sum, c) => sum + c.unread_count, 0);

  return {
    contacts,
    contactsLoading,
    contactsError,
    conversations,
    conversationsLoading,
    unreadCount,
  };
};

/**
 * Hook para mensagens de uma conversa específica.
 * Paginado: carrega as últimas 50; `loadOlder` puxa mais 50 (auditoria A2).
 */
export const useChatMessages = (recipientId: string | null) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [messageLimit, setMessageLimit] = useState(MESSAGES_PAGE_SIZE);

  const { data: messages = [], isLoading } = useQuery({
    queryKey: ['chat-messages', recipientId, messageLimit],
    queryFn: async () => {
      if (!user || !recipientId) return [];

      const { data, error } = await db
        .from('chat_messages')
        .select('*')
        .or(
          `and(sender_id.eq.${user.id},recipient_id.eq.${recipientId}),and(sender_id.eq.${recipientId},recipient_id.eq.${user.id})`
        )
        .order('created_at', { ascending: false })
        .limit(messageLimit);

      if (error) throw error;
      // Query busca as N mais recentes (desc); exibição é cronológica
      return ((data || []) as ChatMessage[]).reverse();
    },
    enabled: !!user && !!recipientId,
    staleTime: 30000, // Realtime atualiza via useChatSync
  });

  const hasOlder = messages.length >= messageLimit;
  const loadOlder = () => setMessageLimit((l) => l + MESSAGES_PAGE_SIZE);

  // Marcar mensagens como lidas ao abrir conversa
  const markAsRead = useMutation({
    mutationFn: async () => {
      if (!user || !recipientId) return;

      const { error } = await db
        .from('chat_messages')
        .update({ is_read: true })
        .eq('recipient_id', user.id)
        .eq('sender_id', recipientId)
        .eq('is_read', false);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
    },
  });

  // Enviar mensagem (sender_id é imposto pelo servidor a partir do JWT)
  const sendMessage = useMutation({
    mutationFn: async ({
      content,
      attachment
    }: {
      content?: string;
      attachment?: { path: string; name: string; type: string }
    }) => {
      if (!user || !recipientId) throw new Error('Não autenticado');
      if (!content && !attachment) throw new Error('Mensagem vazia');

      const messageData = {
        recipient_id: recipientId,
        content: content || null,
        attachment_path: attachment?.path || null,
        attachment_name: attachment?.name || null,
        attachment_type: attachment?.type || null,
        attachment_expires_at: attachment
          ? new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
          : null,
      };

      const { data, error } = await db
        .from('chat_messages')
        .insert(messageData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-messages', recipientId] });
      queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
    },
    onError: (error) => {
      console.error('Erro ao enviar mensagem:', error);
      toast.error('Erro ao enviar mensagem');
    },
  });

  // Upload de anexo
  const uploadAttachment = async (file: File): Promise<{ path: string; name: string; type: string } | null> => {
    if (!user) return null;

    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

    const { error } = await storage
      .from('chat-files')
      .upload(fileName, file);

    if (error) {
      console.error('Erro no upload:', error);
      toast.error('Erro ao enviar arquivo');
      return null;
    }

    return {
      path: fileName,
      name: file.name,
      type: file.type,
    };
  };

  // Download de anexo (gera signed URL)
  const getAttachmentUrl = async (path: string): Promise<string | null> => {
    const { data, error } = await storage
      .from('chat-files')
      .createSignedUrl(path, 3600); // 1 hora

    if (error) {
      console.error('Erro ao gerar URL:', error);
      return null;
    }

    return data.signedUrl;
  };

  return {
    messages,
    isLoading,
    hasOlder,
    loadOlder,
    sendMessage,
    markAsRead,
    uploadAttachment,
    getAttachmentUrl,
  };
};
